import { useApolloClient, useSubscription } from '@apollo/client';
import { FC, useEffect, useMemo } from 'react';
import { GET_ACTIVE_GATE_MOVEMENTS } from '../graphql/queries/getActiveGateMovements';
import { GATE_MESSAGE_RECEIVED } from '../graphql/subscriptions/gateMessageReceived';
import { changesActiveMembership, createThrottle } from '../utils/activeGateMovements';
import { getMovementFragmentData } from '../utils/getMovementFragmentData';
import { refreshGateMovementsHistory } from '../utils/refreshGateMovementsHistory';

/** Ventana del refetch de la lista del banner: colapsa las ráfagas de mensajes (AMARU-911). */
export const ACTIVE_MOVEMENTS_REFETCH_WINDOW_MS = 3_000;

/**
 * Tope del retardo aleatorio antes de cada refetch. El mismo mensaje llega a la vez a todas las
 * sesiones que ven la compuerta (en la organización 2, todas): sin esto refetchean en la misma
 * ráfaga. Menor que la ventana, así que no se solapa con el refetch final del throttle.
 */
export const ACTIVE_MOVEMENTS_REFETCH_JITTER_MS = 1_500;

interface Props {
  /**
   * Acota la suscripción a una sola compuerta (AMARU-862/US-006). Sin este prop se mantiene el
   * comportamiento histórico — entrega mensajes de toda compuerta accesible — que es lo que usa
   * el montaje global (`GlobalComponents`) para refrescar tableros con varias compuertas a la
   * vez. El modal de operación monta una instancia aparte con su propio `gateId`: no reemplaza
   * al montaje global, lo complementa para ese consumidor puntual.
   */
  gateId?: string;
}

export const GateMessagesSubscription: FC<Props> = ({ gateId }) => {
  const client = useApolloClient();

  const activeMovementsRefetch = useMemo(() => {
    let jitterTimer: ReturnType<typeof setTimeout> | null = null;
    const throttle = createThrottle(() => {
      jitterTimer = setTimeout(() => {
        jitterTimer = null;
        void client.refetchQueries({ include: [GET_ACTIVE_GATE_MOVEMENTS] });
      }, Math.random() * ACTIVE_MOVEMENTS_REFETCH_JITTER_MS);
    }, ACTIVE_MOVEMENTS_REFETCH_WINDOW_MS);
    return {
      call: throttle.call,
      cancel() {
        throttle.cancel();
        if (jitterTimer) clearTimeout(jitterTimer);
        jitterTimer = null;
      },
    };
  }, [client]);
  useEffect(() => () => activeMovementsRefetch.cancel(), [activeMovementsRefetch]);

  useSubscription(GATE_MESSAGE_RECEIVED, {
    variables: { gateId },
    onData({ data }) {
      const message = data.data?.gateMessageReceived;
      const messageGateId = message?.movements.id;
      if (!message || !messageGateId) return;

      void refreshGateMovementsHistory(client, messageGateId);

      // Solo la instancia global: la del modal de operación vería los mismos mensajes y
      // duplicaría el refetch.
      if (gateId != null) return;
      // Sin lista en cache (todavía no cargó, o su primera carga falló) no hay contra qué comparar:
      // todo GO06 parecería «no listado» y cada sesión refetchearía cada 3 s mientras haya
      // compuertas en movimiento. La lista llega por su propia carga o por el refresco de foco.
      const listed = client.readQuery({ query: GET_ACTIVE_GATE_MOVEMENTS })?.activeGateMovements;
      if (!listed) return;
      const activeIds = new Set(listed.map((movement) => movement.id));
      const movements = [message.movements.activeMovement, message.movement].map((movement) =>
        movement ? getMovementFragmentData(movement) : null,
      );
      if (changesActiveMembership(activeIds, movements)) activeMovementsRefetch.call();
    },
  });

  return null;
};
